import { useCallback, useMemo, useRef, useState } from 'react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import { zipPdfFiles } from '../split/splitPdfEngine';
import { convertPdfToExcel } from './pdfToExcelEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolCard from '../../components/common/ToolCard';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

const SHEET_OPTIONS = [
    { id: 'perPage', labelKey: 'pdfToExcel.sheetModes.perPage', descKey: 'pdfToExcel.sheetModes.perPageDesc' },
    { id: 'single', labelKey: 'pdfToExcel.sheetModes.single', descKey: 'pdfToExcel.sheetModes.singleDesc' },
];

export default function PdfToExcel() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [sheetMode, setSheetMode] = useState('perPage');
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        if (!list.length) return;

        setFiles((prev) => {
            const next = [...prev];
            for (const f of list) {
                if (!(f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'))) {
                    const msg = t('pdfToExcel.errors.invalidType');
                    setError(msg);
                    notifications.show({ title: t('common.error'), message: msg, color: 'red' });
                    continue;
                }
                if (f.size > MAX_FILE_BYTES) {
                    const msg = t('pdfToExcel.errors.tooLarge');
                    setError(msg);
                    notifications.show({ title: t('common.error'), message: msg, color: 'red' });
                    continue;
                }
                next.push(f);
            }
            return next;
        });
        setResults([]);
        setError(null);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in.
    usePdfPaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviewIndex((prev) => (index < prev ? prev - 1 : Math.max(0, Math.min(prev, files.length - 2))));
        setResults([]);
        setError(null);
    };

    const clearResultsOnly = () => {
        setResults([]);
        setError(null);
        setProgress(null);
    };

    const clearAll = () => {
        setFiles([]);
        setResults([]);
        setProgress(null);
        setError(null);
        setPreviewIndex(0);
    };

    const handleConvert = async () => {
        if (!files.length || processing) return;

        recordToolUsage('pdf/pdf-to-excel', { category: 'pdf' });

        setProcessing(true);
        setResults([]);
        setProgress(null);
        setError(null);

        const done = [];
        let failures = 0;
        let passwordProtected = 0;
        let noText = 0;

        for (const file of files) {
            try {
                const blob = await convertPdfToExcel(
                    file,
                    { sheetMode },
                    (page, total) => setProgress({ page, total, fileName: file.name }),
                );
                done.push({
                    name: `${file.name.replace(/\.pdf$/i, '')}.xlsx`,
                    blob,
                });
            } catch (err) {
                if (err?.message === 'password') passwordProtected += 1;
                else if (err?.message === 'noText') noText += 1;
                else failures += 1;
            }
        }

        setResults(done);
        setProcessing(false);
        setProgress(null);

        let latestError = null;
        if (passwordProtected) {
            latestError = t('pdfToExcel.errors.password');
            notifications.show({ title: t('common.error'), message: latestError, color: 'red' });
        }
        if (noText) {
            latestError = t('pdfToExcel.errors.noText');
            notifications.show({ title: t('common.error'), message: latestError, color: 'red' });
        }
        if (failures) {
            latestError = t('pdfToExcel.errors.failed');
            notifications.show({ title: t('common.error'), message: latestError, color: 'red' });
        }
        if (done.length) {
            notifications.show({ title: t('common.success'), message: t('pdfToExcel.done'), color: 'green' });
        }
        setError(done.length ? null : latestError);
    };

    const canConvert = files.length > 0 && !processing;

    const progressLabel = progress
        ? t('pdfToExcel.progress', { page: toBnDigits(progress.page), total: toBnDigits(progress.total) })
        : '';

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun: canConvert,
        processing,
        hasResult: results.length > 0,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('pdfToExcel.done'),
        error: error || t('toolPage.statusText.error'),
    });
    const progressPercent = progress?.total
        ? Math.round((progress.page / progress.total) * 100)
        : undefined;
    const successLabel = results.length > 1
        ? `${t('pdfToExcel.resultTitle')} (${toBnDigits(results.length)})`
        : t('pdfToExcel.done');
    const previewLabels = files.slice(0, 5).map((file) => file.name.replace(/\.pdf$/i, ''));
    const fileMeta = (file) => formatFileSize(file.size);
    const hasBatchResults = results.length > 1;
    const previewDocLabels = hasBatchResults
        ? results.slice(0, 5).map((item) => item.name.replace(/\.xlsx$/i, ''))
        : previewLabels;
    const previewDocCount = hasBatchResults
        ? Math.min(Math.max(results.length, 1), 5)
        : Math.min(Math.max(files.length, 1), 5);

    const resultDownloads = useMemo(() => {
        if (results.length <= 1) return undefined;
        return results.map((r) => ({
            id: r.name,
            name: r.name,
            meta: formatFileSize(r.blob.size),
            onDownload: () => downloadBlob(r.blob, r.name),
        }));
    }, [results]);

    return (
        <ToolPageLayout
            slug="pdf/pdf-to-excel"
            subtitleKey="pdfToExcel.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(files.length) })}
            showAdd={files.length > 0}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={files.length > 0 || results.length > 0 || error ? clearAll : undefined}
            primaryAction={{
                label: t('pdfToExcel.convert'),
                onClick: handleConvert,
                disabled: !files.length || processing || results.length > 0,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {files.length === 0 ? (
                        <PdfDropZone
                            title={t('pdfToExcel.dropTitle')}
                            hint={t('pdfToExcel.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <div className="max-h-[220px] space-y-2 overflow-y-auto pr-1 [scrollbar-width:thin]">
                            {files.map((file, index) => (
                                <PdfUploadedFile
                                    key={`${file.name}-${index}`}
                                    name={file.name}
                                    meta={fileMeta(file)}
                                    selected={previewIndex === index}
                                    onSelect={() => setPreviewIndex(index)}
                                    onRemove={() => removeFile(index)}
                                    removeLabel={t('pdfToExcel.removeFile')}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
            settings={(
                <>
                    {files.length > 0 && (
                        <ToolCard title={t('pdfToExcel.sheetTitle')}>
                            <div className="space-y-3">
                                <div className="flex flex-col gap-2">
                                    {SHEET_OPTIONS.map(({ id, labelKey, descKey }) => (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => {
                                                setSheetMode(id);
                                                setResults([]);
                                                setError(null);
                                            }}
                                            className={`rounded-xl border px-3.5 py-3 text-left transition-colors ${
                                                sheetMode === id
                                                    ? 'border-primary bg-primary/10'
                                                    : 'border-zinc-200 dark:border-zinc-700'
                                            }`}
                                        >
                                            <p className={`text-[13px] font-semibold ${sheetMode === id ? 'text-primary' : 'text-zinc-800 dark:text-zinc-100'}`}>
                                                {t(labelKey)}
                                            </p>
                                            <p className="mt-0.5 text-xs text-zinc-400 dark:text-zinc-500">{t(descKey)}</p>
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs leading-relaxed text-zinc-400 dark:text-zinc-500">{t('pdfToExcel.textHint')}</p>
                            </div>
                        </ToolCard>
                    )}

                    {hasBatchResults && !processing && !error ? (
                        <ToolCard title={t('pdfToExcel.resultTitle')}>
                            <div className="space-y-2">
                                {results.map((item) => (
                                    <div
                                        key={item.name}
                                        className="flex items-center gap-2 rounded-xl border border-[#ECE8E4] bg-white px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-[13px] font-semibold text-[#1C1815] dark:text-zinc-100">{item.name}</p>
                                            <p className="text-[11px] text-[#A8A099] dark:text-zinc-500">{formatFileSize(item.blob.size)}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => downloadBlob(item.blob, item.name)}
                                            className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
                                        >
                                            {t('pdfToExcel.download')}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </ToolCard>
                    ) : null}
                </>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={progressLabel || t('pdfToExcel.converting')}
                    progress={progressPercent}
                    error={error}
                    successLabel={successLabel}
                    downloadLabel={t('pdfToExcel.download')}
                    downloads={resultDownloads}
                    // The sticky bar's one button fetches every sheet at once.
                    bundleDownload={results.length > 1 ? {
                        label: t('toolPage.downloadAll'),
                        onClick: async () => downloadBlob(await zipPdfFiles(results), 'pdf-to-excel.zip'),
                    } : undefined}
                    onDownload={results.length === 1
                        ? () => downloadBlob(results[0].blob, results[0].name)
                        : undefined}
                    onEditAgain={results.length > 0 ? clearResultsOnly : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={results.length > 0 ? clearAll : undefined}
                    againLabel={t('pdfToExcel.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('pdfToExcel.convert')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={files.length === 0 && !error}
                    pdfPreview={((files.length > 0) || hasBatchResults) && !processing && !error}
                >
                    {((files.length > 0) || hasBatchResults) && !processing && !error ? (
                        <PdfDocPreview
                            files={files}
                            activeIndex={previewIndex}
                            onActiveIndexChange={setPreviewIndex}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
